import { useEffect, useRef, useState } from 'react';
import { AUTONOMY_LABELS } from '@cm/protocol';
import { paneColor, paneName } from '../layout';
import { useActiveLayout, useActiveWorkspace, useStore, useWorkspaceSessions } from '../store';
import { SIDEBAR_HOVER_DELAY_MS, agentColor } from '../theme';
import { AgentMark } from './AgentMark';

interface Props {
  onEditWorkspace: () => void;
  /** No toque não existe hover; o botão do menu abre a lista. */
  allowHover?: boolean;
  forcedOpen?: boolean;
  /** Fecha o menu do celular depois de escolher um agente. */
  onNavigated?: () => void;
}

export function Sidebar({
  onEditWorkspace,
  allowHover = true,
  forcedOpen = false,
  onNavigated,
}: Props) {
  const agents = useStore((s) => s.agents);
  const focusedId = useStore((s) => s.focusedId);
  const selectedIds = useStore((s) => s.selectedIds);
  const focus = useStore((s) => s.focus);
  const toggleSelected = useStore((s) => s.toggleSelected);
  const selectAll = useStore((s) => s.selectAll);
  const clearSelection = useStore((s) => s.clearSelection);

  const workspace = useActiveWorkspace();
  const sessions = useWorkspaceSessions();
  // A lista aqui ja e filtrada pela aba aberta (useWorkspaceSessions), entao o
  // layout ativo e o certo para ela. Quando nao ha aba, o layout vem vazio e o
  // paneName cai no title -- que e exatamente o comportamento desejado.
  const layout = useActiveLayout();
  const tokens = useStore((s) => s.theme.tokens);
  const pinned = useStore((s) => s.sidebarPinned);
  const togglePinned = useStore((s) => s.toggleSidebarPinned);

  const [hovering, setHovering] = useState(false);
  const timerRef = useRef<number | null>(null);

  // Limpa o timer ao desmontar: abrir a sidebar depois que o componente saiu
  // deixaria o estado preso e a sobreposicao aberta sem nada por baixo.
  useEffect(() => () => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
  }, []);

  function agendarAbertura() {
    if (!allowHover || pinned || timerRef.current !== null) return;
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      setHovering(true);
    }, SIDEBAR_HOVER_DELAY_MS);
  }

  function cancelarEFechar() {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setHovering(false);
  }

  // No celular o hover nao existe e a sidebar fixada cobriria o terminal.
  // So o botao do menu (forcedOpen) abre a lista.
  const open = forcedOpen || (allowHover && (pinned || hovering));

  return (
    <aside
      className={`sidebar${open ? ' open' : ''}${pinned ? ' pinned' : ''}`}
      onMouseEnter={agendarAbertura}
      onMouseLeave={cancelarEFechar}
      // Hover nao existe no touch e nao chega pelo teclado; o foco dentro da
      // sidebar abre pelo mesmo caminho.
      onFocusCapture={() => {
        if (allowHover) setHovering(true);
      }}
    >
      {/* Faixa recolhida: as marcas dos agentes com o ponto de status, na cor
          do pane. E o que sustenta o "bati o olho e sei o que esta rodando"
          sem precisar abrir nada. */}
      <div className="sidebar-rail" aria-hidden={open}>
        {workspace && (
          <span
            className="dot"
            style={{ background: workspace.color, width: 7, height: 7 }}
            title={workspace.name}
          />
        )}
        {sessions.map((session) => (
          <button
            key={session.id}
            className="rail-item"
            style={{
              ['--agent-color' as string]:
                paneColor(layout, session.id) ?? agentColor(tokens, session.agent),
            }}
            onClick={() => {
              focus(session.id);
              onNavigated?.();
            }}
            title={paneName(layout, session.id, session.title)}
          >
            <AgentMark agent={session.agent} size={15} />
            <span
              className="rail-status"
              style={{
                color:
                  session.status === 'running'
                    ? 'var(--ok)'
                    : session.status === 'starting'
                      ? 'var(--warn)'
                      : 'var(--danger)',
              }}
            >
              ●
            </span>
          </button>
        ))}
      </div>

      <div className="sidebar-body">
        <div className="row" style={{ marginBottom: 4 }}>
          <span className="grow" />
          <button
            className={`ghost pin-btn${pinned ? ' on' : ''}`}
            onClick={togglePinned}
            title={pinned ? 'Soltar: volta a abrir no hover' : 'Fixar aberta'}
            aria-pressed={pinned}
          >
            {pinned ? '📌' : '📍'}
          </button>
        </div>
      {workspace && (
        <>
          <div className="section-label">Projeto</div>
          <div className="card" style={{ marginBottom: 6, padding: '9px 10px' }}>
            <div className="row">
              <span
                className="dot"
                style={{ background: workspace.color, width: 7, height: 7 }}
              />
              <span className="grow" style={{ fontSize: 12, fontWeight: 500 }}>
                {workspace.name}
              </span>
              <button className="ghost" onClick={onEditWorkspace} title="Editar projeto">
                ⚙
              </button>
            </div>
            <div
              className="card-desc"
              style={{ marginTop: 6, marginBottom: 0, wordBreak: 'break-all' }}
              title={workspace.cwd}
            >
              {workspace.cwd}
            </div>
          </div>
        </>
      )}

      <div className="section-label">Agentes no projeto ({sessions.length})</div>

      {sessions.length === 0 && (
        <p className="empty-hint">
          Nenhum agente aberto aqui. Use <strong>+ Agente</strong> para colocar um Claude, Codex
          ou Gemini a trabalhar nesta pasta.
        </p>
      )}

      {sessions.map((session) => {
        const name = paneName(layout, session.id, session.title);
        const accent = paneColor(layout, session.id) ?? agentColor(tokens, session.agent);
        return (
        <div
          key={session.id}
          className={`session-item${session.id === focusedId ? ' focused' : ''}`}
          style={{ ['--agent-color' as string]: accent }}
          onClick={() => {
            focus(session.id);
            onNavigated?.();
          }}
        >
          <input
            type="checkbox"
            checked={selectedIds.includes(session.id)}
            onChange={() => toggleSelected(session.id)}
            onClick={(e) => e.stopPropagation()}
            title="Marcar para receber broadcast de prompt"
          />
          <div className="meta">
            <div className="name" title={name}>
              <AgentMark agent={session.agent} size={12} />
              {name}
            </div>
            <div className="sub">
              <span
                style={{
                  color:
                    session.status === 'running'
                      ? 'var(--ok)'
                      : session.status === 'starting'
                        ? 'var(--warn)'
                        : 'var(--danger)',
                }}
              >
                {session.status === 'running' ? '●' : session.status === 'starting' ? '◌' : '○'}
              </span>
              <span>{AUTONOMY_LABELS[session.autonomy]}</span>
            </div>
          </div>
        </div>
        );
      })}

      {sessions.length > 1 && (
        <div className="row" style={{ marginTop: 7 }}>
          <button className="ghost" onClick={selectAll}>
            marcar ativas
          </button>
          <button className="ghost" onClick={clearSelection}>
            limpar
          </button>
        </div>
      )}

      <div className="section-label">Instalados na máquina</div>
      {agents.map((agent) => (
        <div className="agent-row" key={agent.agent}>
          <AgentMark agent={agent.agent} size={13} />
          <span className="grow">{agent.label}</span>
          {agent.installed ? (
            <span title={agent.path ?? ''}>{compactVersion(agent.version)}</span>
          ) : (
            <span className="off">ausente</span>
          )}
        </div>
      ))}
      </div>
    </aside>
  );
}

/** As versões vêm como "2.1.224 (Claude Code)"; na sidebar só o número cabe. */
function compactVersion(version: string | null): string {
  if (!version) return 'ok';
  const match = version.match(/\d+\.\d+\.\d+/);
  return match ? match[0] : version.slice(0, 12);
}
